import React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { RootState } from 'app/session/store'
import { authenticate, getMerchantParams } from 'app/api'
import { initialize, updateAuth, updateMerchantParams } from 'app/session/actions'
import InvalidMerchant from 'views/screens/errors/InvalidMerchant'
import { Spinner } from 'components'
import { makeStyles } from '@material-ui/core'
import { useEffectAfter } from 'app/utils'
import { Color } from 'theme/style'

const useStyles = makeStyles((theme) => ({
  main: {
    height: 'auto',
    minHeight: '100vh',
    width: '100vw',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
}))

type AuthenticatorProps = {
  merchant: string
  children?: React.ReactNode,
}

const Authenticator = (props: AuthenticatorProps) => {
  const dispatch = useDispatch()
  const session = useSelector((state: RootState) => state.session)
  const classes = useStyles()
  const locale = 'it_IT'

  const [userIsValid, setUserIsValid] = React.useState<boolean | null>(null)
  const [merchantParamsAvailable, setMerchantParamsAvailable] = React.useState<boolean | null>(null)
  const [applicationLogged, setApplicationLogged] = React.useState(false)

  React.useEffect(() => {
    authenticateMerchant()
  }, [])


  function authenticateMerchant() {
    if (userIsValid === true) return
    if (session.auth != null) return

    const params = {
      merchantCode: props.merchant
    }
    authenticate(params, session.config, {
      response(response) {
        let data = response.data

        const urlPrefixes = ['http://', 'https://']
        if (urlPrefixes.some(v => data.merchantSiteUrl?.includes(v, 0)) === false) data.merchantSiteUrl = 'https://' + data.merchantSiteUrl

        dispatch(updateAuth(data))
      },
      error(error) {
        setUserIsValid(false)
      }
    })
  }

  function downloadParameters() {
    if (session.merchantParams != null) return
    if (session.auth == null || session.auth.shopNameWhiteLabel == null) return
    if (session.config?.app.merchantUrl == null) {
      setMerchantParamsAvailable(false)
      return
    }

    const params = {
      locale: locale,
      merchantName: session.auth.shopNameWhiteLabel
    }
    getMerchantParams(params, session.config, {
      response(response) {
        dispatch(updateMerchantParams(response))
      },
      error(error) {
        setMerchantParamsAvailable(false)
      }
    })
  }

  function logApplicationData() {
    if (session.initialized === true) return

    const name = process.env.REACT_APP_NAME
    const version = process.env.REACT_APP_VERSION
    const config = session.config?.type
    const i18n = session.i18n != null ? 'remote' : 'local'
    const merchant = merchantParamsAvailable === true ? 'available' : 'unavailable'

    console.log('Initialized application "' + name + '" v' + version)
    if (session.config == null) console.log('- Configuration unavailable')
    else console.log('- Configuration data: ' + config)
    console.log('- i18n data: ' + i18n)
    console.log('- Merchant params: ' + merchant)

    setApplicationLogged(true)
  }

  React.useEffect(() => {
    if (session.auth == null) return
    downloadParameters()
    setUserIsValid(true)
  }, [session.auth])

  React.useEffect(() => {
    if (session.merchantParams == null) return
    setMerchantParamsAvailable(true)
  }, [session.merchantParams])

  useEffectAfter(() => {
    if (applicationLogged === true) dispatch(initialize(true))
  }, [applicationLogged])

  useEffectAfter(() => {
    logApplicationData()
  }, [merchantParamsAvailable])

  const mainStyle = {
    backgroundColor: session.merchantParams?.style?.backgroundColor ?? Color.Background
  }

  if (userIsValid === false) return (
    <InvalidMerchant />
  )

  if (userIsValid == null || merchantParamsAvailable == null) return (
    <div className={classes.main} style={mainStyle}>
      <Spinner />
    </div>
  )

  return (
    <div className={classes.main} style={mainStyle}>
      {props.children}
    </div>
  )
}

export default Authenticator
